import { deg2rad } from "../math/angles";
import { fmt } from "../format/number";
import { ENGINE_DEFAULTS } from "../defaults";
import type { CheckSettings, FoundationSoil } from "../types/input";
import type { BasePressureResult, BearingResult, CaseSums, CheckResult, EccentricityResult } from "../types/result";
import type { WarningCode } from "../warnings";

export type Warn = (code: WarningCode, context?: string) => void;
const T = ENGINE_DEFAULTS.checks.tbdy;
const okText = (ok: boolean): string => (ok ? "SAĞLIYOR" : "SAĞLAMIYOR");
const pick = (p: { static: number; seismic: number }, seismic: boolean): number => (seismic ? p.seismic : p.static);

export function checkOverturning(sums: CaseSums, isSeismic: boolean, checks: CheckSettings): CheckResult {
  const gammaR = isSeismic ? checks.tbdy.gammaRdevSeismic : checks.tbdy.gammaRdev;
  const design = sums.Mres / gammaR;
  const tOk = sums.Movr <= design + 1e-9;
  const target = pick(checks.classic.overturning, isSeismic);
  const factor = sums.Movr > 1e-12 ? sums.Mres / sums.Movr : Infinity;
  const cOk = factor >= target;
  return {
    tbdy: { effect: sums.Movr, resistance: sums.Mres, gammaR, designResistance: design, ratio: design > 0 ? sums.Movr / design : Infinity, ok: tOk,
      verdict: `${okText(tOk)} — Edev = ${fmt(sums.Movr)} ≤ Rdev/γRdev = ${fmt(sums.Mres)}/${fmt(gammaR, 2)} = ${fmt(design)} kNm/m` },
    classic: { effect: sums.Movr, resistance: sums.Mres, factor, target, ok: cOk,
      verdict: `${okText(cOk)} — GK = Mr/Mo = ${fmt(sums.Mres)}/${fmt(sums.Movr)} = ${fmt(factor)} ${cOk ? "≥" : "<"} ${fmt(target, 2)}` },
  };
}

/**
 * Eğik taban (tanα = s, topuk alçak) izdüşüm katsayıları. s = 0 → cos = 1, sin = 0 tam (yatay taban sonuçları bit bit aynı).
 * Taban boyunca burun yönü (+) birim vektör (−cosα, sinα), tabana dik (zemine doğru) (−sinα, −cosα); ΣH burun yönünde +, N aşağı +:
 *   N' = N·cosα + H·sinα ,  T' = H·cosα − N·sinα   (docs/research/11-egik-taban.md)
 */
export function baseFrame(slope: number): { cos: number; sin: number } {
  const cos = 1 / Math.sqrt(1 + slope * slope);
  return { cos, sin: slope * cos };
}

/** TBDY profilinde klasik devrilme/kayma GK hüküm vermez, yalnız raporlanır (CheckSettings.classicGoverns = false) */
export function classicInformative(checks: CheckSettings): boolean {
  return checks.designCode === "tbdy2018" && checks.classicGoverns === false;
}

export function eccentricityAndBasePressure(sums: CaseSums, Bh: number, isSeismic: boolean, checks: CheckSettings, warn: Warn, slope = 0): { ecc: EccentricityResult; base: BasePressureResult } {
  if (sums.N <= 1e-9) throw new Error("Net düşey kuvvet sıfır veya negatif (kaldırma ağırlığı aşıyor)");
  // Eğik tabanda bileşke taban doğrusunu burundan t = (ΣMr − ΣMo)/N' uzaklıkta keser (moment burun etrafında; kuvvet (−H, −N)
  // taban noktası (t·cosα, −t·sinα) → M = t·(N·cosα + H·sinα)); taban boyu B = B_yatay/cosα
  const { cos, sin } = baseFrame(slope);
  const N = sums.N * cos + sums.Hdrive * sin;
  const B = Bh / cos;
  if (N <= 1e-9) throw new Error("Taban düzlemine dik kuvvet sıfır veya negatif");
  const xbar = (sums.Mres - sums.Movr) / N;
  const e = B / 2 - xbar;
  // Klasik GK bilgi amaçlıysa (TBDY, classicGoverns false) taban dış merkezliliği en çok B/3 (EN 1997-1 §6.5.4; TBDY'de hüküm yok)
  const divisor = classicInformative(checks) ? Math.min(pick(checks.classic.eccentricity, isSeismic), 3) : pick(checks.classic.eccentricity, isSeismic);
  const limit = B / divisor;
  const outside = xbar <= 0 || xbar >= B;
  if (outside) warn("RESULTANT_OUTSIDE");
  const ae = Math.abs(e);
  const Beff = Math.max(0, B - 2 * ae);
  const ecc: EccentricityResult = { xbar, e, limit, limitDivisor: divisor, ok: !outside && ae <= limit + 1e-9, B, Beff };
  if (slope > 0) ecc.inclined = { slope, alphaDeg: (Math.atan(slope) * 180) / Math.PI, N, T: sums.Hdrive * cos - sums.N * sin, length: B };
  let base: BasePressureResult;
  if (outside) {
    base = { qToe: Infinity, qHeel: 0, contactLength: 0, qMeyerhof: Infinity, tensionRedistributed: true };
  } else if (ae <= B / 6 + 1e-9) {
    const q = N / B;
    base = { qToe: q * (1 + (6 * e) / B), qHeel: q * (1 - (6 * e) / B), contactLength: B, qMeyerhof: N / Beff, tensionRedistributed: false };
  } else {
    const Lc = 3 * (B / 2 - ae);
    const qmax = (2 * N) / Lc;
    base = { qToe: e > 0 ? qmax : 0, qHeel: e > 0 ? 0 : qmax, contactLength: Lc, qMeyerhof: N / Beff, tensionRedistributed: true };
    warn("TENSION_REDISTRIBUTED");
  }
  return { ecc, base };
}

export function baseFrictionAngle(fs: FoundationSoil): number {
  const phi = deg2rad(fs.phi);
  switch (fs.baseFriction.mode) {
    case "phi": return phi;
    case "custom": return deg2rad(fs.baseFriction.value ?? (2 * fs.phi) / 3);
    case "concrete": return Math.atan(0.6);   // grobeton / beton temel: tanδ = 0,6 (TBDY Tablo 16.3)
    default: return (2 * phi) / 3;
  }
}

export function checkSliding(sums: CaseSums, ecc: EccentricityResult, base: BasePressureResult, isSeismic: boolean, checks: CheckSettings, fs: FoundationSoil, warn: Warn, slope = 0): CheckResult {
  const frontWater = Math.max(0, sums.Hresist - sums.Rpk);
  // Eğik taban: kayma taban düzlemi boyunca. Etki T' = ΣH·cosα − N·sinα; sürtünme N'·tanδ ile, N' = N·cosα + (ΣH − ΣH_direnç)·sinα
  // (pasif / ön su yatay dirençlerinin tabana dik bileşeni tam değerle düşülür — güvenli yanda); pasif ve ön su dirençleri taban
  // doğrultusuna izdüşürülür (×cosα). s = 0 → N' = N, T' = ΣH (yatay taban, TBDY Denk. 16.9–16.12 aynen).
  const { cos, sin } = baseFrame(slope);
  const N = sums.N * cos + (sums.Hdrive - sums.Hresist) * sin;
  const Hd = sums.Hdrive * cos - sums.N * sin;
  const tanD = Math.tan(baseFrictionAngle(fs));
  // TBDY 2018 Denk. 16.9–16.12
  let RthChar: number;
  if (fs.drained) {
    const capped = Math.min(tanD, T.tanDeltaMax);
    if (tanD > T.tanDeltaMax + 1e-12) warn("TAND_CAPPED");
    RthChar = N * capped;
    if (checks.tbdy.adhesionInSliding && fs.adhesion > 0) { RthChar += fs.adhesion * base.contactLength; warn("ADHESION_IN_SLIDING"); }
  } else {
    RthChar = base.contactLength * fs.c;
  }
  const Rth = RthChar / T.gammaRh;
  const Rpt = sums.Rpk / T.gammaRp;
  if (sums.Rpk > 1e-9) warn("PASSIVE_IGNORED_TBDY");
  const design = Rth + T.passiveShare * Rpt * cos + frontWater * cos;
  const tOk = Hd <= design + 1e-9;
  // Klasik
  const cRes = (fs.drained ? N * tanD + fs.adhesion * ecc.Beff : base.contactLength * fs.c) + sums.Rpk * cos + frontWater * cos;
  const target = pick(checks.classic.sliding, isSeismic);
  const factor = Hd > 1e-12 ? cRes / Hd : Infinity;
  const cOk = factor >= target;
  const incl = slope > 0 ? ` (eğik taban tanα = ${fmt(slope, 3)}: T' = ΣH·cosα − N·sinα, N' = ${fmt(N)} kN/m)` : "";
  return {
    tbdy: { effect: Hd, resistance: RthChar + sums.Rpk * cos, gammaR: T.gammaRh, designResistance: design, ratio: design > 0 ? Hd / design : Infinity, ok: tOk,
      verdict: `${okText(tOk)} — ${slope > 0 ? "T'" : "Vth"} = ${fmt(Hd)} ≤ Rth + 0,3·Rpt = ${fmt(Rth)} + ${fmt(T.passiveShare * Rpt * cos)} = ${fmt(design)} kN/m${incl}` },
    classic: { effect: Hd, resistance: cRes, factor, target, ok: cOk,
      verdict: `${okText(cOk)} — GK = ΣFr/ΣFh = ${fmt(cRes)}/${fmt(Hd)} = ${fmt(factor)} ${cOk ? "≥" : "<"} ${fmt(target, 2)}${incl}` },
  };
}

export function resolveBearingValues(fs: FoundationSoil, warn: Warn): { qk: number; sigmaAllow: number } {
  const b = fs.bearing;
  if (b.qk !== undefined && b.sigmaAllow !== undefined) return { qk: b.qk, sigmaAllow: b.sigmaAllow };
  if (b.qk !== undefined) { warn("BEARING_DERIVED"); return { qk: b.qk, sigmaAllow: b.qk / b.ratio }; }
  if (b.sigmaAllow !== undefined) { warn("BEARING_DERIVED"); return { qk: b.sigmaAllow * b.ratio, sigmaAllow: b.sigmaAllow }; }
  throw new Error("Taşıma gücü için qk veya zemin emniyet gerilmesi girilmelidir");
}

export function checkBearing(base: BasePressureResult, isSeismic: boolean, checks: CheckSettings, qk: number, sigmaAllow: number, source: "given" | "computed"): BearingResult {
  const design = qk / T.gammaRv;
  const tOk = base.qMeyerhof <= design + 1e-9;
  const f = pick(checks.classic.bearing, isSeismic);
  const allow = sigmaAllow * f;
  const cOk = base.qToe <= allow + 1e-9;
  const factor = base.qToe > 1e-12 ? allow / base.qToe : Infinity;
  return {
    qk, sigmaAllow, qkSource: source,
    tbdy: { effect: base.qMeyerhof, resistance: qk, gammaR: T.gammaRv, designResistance: design, ratio: design > 0 ? base.qMeyerhof / design : Infinity, ok: tOk,
      verdict: `${okText(tOk)} — qo = N/B' = ${fmt(base.qMeyerhof)} ≤ qk/γRv = ${fmt(qk)}/${fmt(T.gammaRv, 2)} = ${fmt(design)} kN/m²` },
    classic: { effect: base.qToe, resistance: allow, factor, target: 1.0, ok: cOk,
      verdict: `${okText(cOk)} — qmax = ${fmt(base.qToe)} ${cOk ? "≤" : ">"} ${f === 1 ? "" : fmt(f, 2) + "·"}σem = ${fmt(allow)} kN/m²` },
  };
}
